/*
 * The single source of truth for animation in the web app. Every component
 * transition goes through these helpers so timings stay consistent and
 * `prefers-reduced-motion` is honoured everywhere. See web/docs/ui-ux.md §4.
 *
 * Do not inline durations/springs in components. If you need a new pattern,
 * add it here and document it in ui-ux.md §4.
 */
import type { MotionStyle, Transition, Variants } from 'framer-motion';
import { useSyncExternalStore } from 'react';

/**
 * React's DOM types and framer-motion both declare these handler names with
 * incompatible signatures. Strip them from a props type before spreading it
 * into a motion component (see the restyled shadcn primitives).
 */
export type MotionSafeProps<Props> = Omit<
  Props,
  'onAnimationStart' | 'onDragStart' | 'onDrag' | 'onDragEnd'
>;

export const DUR = { fast: 0.12, base: 0.2, slow: 0.3 } as const;
export const EASE_OUT = [0.16, 1, 0.3, 1] as const;

export const SPRING = {
  /** poster → detail shared element */
  shared: { type: 'spring', stiffness: 320, damping: 34 } as Transition,
  /** segmented-control thumb */
  thumb: { type: 'spring', stiffness: 400, damping: 32 } as Transition,
  /** press feedback */
  press: { type: 'spring', stiffness: 500, damping: 30 } as Transition,
  /**
   * the cover flight: a clicked poster flies into the title page's hero and
   * turns into its case pose in the same gesture. Critically damped — it
   * glides in and stops, no overshoot (an overshoot read as a jiggle).
   */
  flight: { type: 'spring', stiffness: 120, damping: 22 } as Transition,
  /** a check mark landing (the episode watched check) — a little overshoot */
  pop: { type: 'spring', stiffness: 600, damping: 15 } as Transition,
} as const;

/** True when the viewer asked for reduced motion. Re-read per render if needed. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

/** Route-change wrapper variants: cross-fade + small rise, or a bare fade. */
export function pageVariants(): Variants {
  if (prefersReducedMotion()) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { duration: 0.08 } },
      exit: { opacity: 0, transition: { duration: 0.08 } },
    };
  }
  return {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { duration: DUR.base, ease: EASE_OUT } },
    exit: { opacity: 0, y: -4, transition: { duration: DUR.fast, ease: EASE_OUT } },
  };
}

/**
 * A one-time opacity fade on mount — Tonight's "now showing" backdrop (its
 * title rises with `pageVariants()`). Opacity only, so reduced motion keeps
 * the same shape, just shorter.
 */
export function fadeIn(): Variants {
  return {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: { duration: prefersReducedMotion() ? 0.08 : DUR.slow, ease: EASE_OUT },
    },
  };
}

/** Longest stagger cascade: 14 items × 60ms (ui-ux.md §4 catalog, "cap at ~14"). */
const STAGGER_STEP = 0.06;
const STAGGER_MAX_TOTAL = 14 * STAGGER_STEP;

/**
 * Parent for a staggered grid/list mount: 60ms between items, but the whole
 * cascade never runs past ~840ms — a 100-cover wall settles in the same time
 * as 14 covers instead of trickling in for seconds.
 */
export function staggerParent(count: number): Variants {
  const step = prefersReducedMotion()
    ? 0
    : Math.min(STAGGER_STEP, STAGGER_MAX_TOTAL / Math.max(count, 1));
  return {
    initial: {},
    animate: { transition: { staggerChildren: step } },
  };
}

/** Child of a staggered list. */
export function staggerChild(): Variants {
  if (prefersReducedMotion()) {
    return { initial: { opacity: 0 }, animate: { opacity: 1, transition: { duration: 0.1 } } };
  }
  return {
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.24, ease: EASE_OUT } },
  };
}

/**
 * Press feedback for controls (buttons, toggles, nav items, switches): a small
 * tap scale only. Controls do not grow on hover — a full-width button that
 * swells under the cursor reads as decoration, not feedback; their hover is a
 * colour/fill change in CSS.
 */
export function pressable() {
  if (prefersReducedMotion()) return {};
  return {
    whileTap: { scale: 0.98 },
    transition: SPRING.press,
  };
}

/**
 * The watched check's glyph (ui-ux.md §4 "Check pop"): springs in from 0.4
 * when `on` flips true, out when false. `initial={false}` so a list that
 * renders already-watched rows doesn't pop them all on mount — only a toggle
 * animates. Reduced motion: no transition, the glyph just shows/hides.
 */
export function checkPop(on: boolean) {
  return {
    initial: false as const,
    animate: on ? { scale: 1, opacity: 1 } : { scale: 0.4, opacity: 0 },
    transition: prefersReducedMotion() ? { duration: 0 } : SPRING.pop,
  };
}

/**
 * Hover lift + press for covers (posters, interactive cards): the thing you
 * open. The lift says "this opens"; keep it off plain controls.
 */
export function liftable() {
  if (prefersReducedMotion()) return {};
  return {
    whileHover: { scale: 1.03 },
    whileTap: { scale: 0.97 },
    transition: SPRING.press,
  };
}

/**
 * A step inside a multi-step wizard (e.g. the desktop import/export dialog):
 * horizontal slide + fade, direction-aware (1 = forward/deeper, -1 = back).
 * Exits opposite the direction it entered from, like a card being swapped out.
 */
export function wizardStep(direction: 1 | -1 = 1): Variants {
  if (prefersReducedMotion()) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { duration: 0.1 } },
      exit: { opacity: 0, transition: { duration: 0.1 } },
    };
  }
  return {
    initial: { opacity: 0, x: direction * 16 },
    animate: { opacity: 1, x: 0, transition: { duration: DUR.base, ease: EASE_OUT } },
    exit: { opacity: 0, x: direction * -16, transition: { duration: DUR.fast, ease: EASE_OUT } },
  };
}

/** Bottom sheet / dialog content variants. */
export function sheetVariants(): Variants {
  if (prefersReducedMotion()) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { duration: 0.1 } },
      exit: { opacity: 0, transition: { duration: 0.1 } },
    };
  }
  return {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.26, ease: EASE_OUT } },
    exit: { opacity: 0, y: 8, transition: { duration: DUR.fast, ease: EASE_OUT } },
  };
}

/*
 * ── Rich-motion tier ────────────────────────────────────────────────────────
 * Showpiece motion (the auth marquee's drift, entrance choreography and
 * pointer parallax — ui-ux.md §11) runs ONLY on a capable desktop: a wide
 * viewport with a fine hovering pointer, no reduced-motion or data-saver
 * request, a non-mobile UA, enough cores/memory, and a modern engine. Phones
 * and tablets never get it, whatever their size. Everything else gets the
 * static composition — never a broken or half-animated one.
 */
export const RICH_MOTION_QUERY =
  '(min-width: 1024px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)';

type CapabilityNavigator = Navigator & {
  connection?: { saveData?: boolean };
  deviceMemory?: number;
  userAgentData?: { mobile?: boolean };
};

export function supportsRichMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  if (!window.matchMedia(RICH_MOTION_QUERY).matches) return false;
  const nav = navigator as CapabilityNavigator;
  if (nav.userAgentData?.mobile) return false;
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(nav.userAgent ?? '')) return false;
  if (nav.connection?.saveData) return false;
  if ((nav.hardwareConcurrency ?? 8) < 4) return false;
  if (nav.deviceMemory !== undefined && nav.deviceMemory < 4) return false;
  // Modern engine: individual transform properties + dynamic viewport units
  // (Chrome 108+, Safari 15.4+, Firefox 101+).
  const css = window.CSS;
  if (!css?.supports?.('translate', '1px') || !css.supports('height', '1dvh')) return false;
  return true;
}

function subscribeRichMotion(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(RICH_MOTION_QUERY);
  mql.addEventListener?.('change', onChange);
  return () => mql.removeEventListener?.('change', onChange);
}

/** Live rich-motion capability — flips off if the window narrows or the OS asks for less motion. */
export function useRichMotion(): boolean {
  return useSyncExternalStore(subscribeRichMotion, supportsRichMotion, () => false);
}

/*
 * ── Load settle ─────────────────────────────────────────────────────────────
 * The one orchestrated "things smoothing down once loaded" sequence
 * (ui-ux.md §4 "Load settle"). Only `<RevealGroup>` / `<Reveal>` /
 * `<RevealFill>` (src/components/Reveal.tsx) consume these — screens never
 * call them.
 *
 * The mode is resolved ONCE when a group mounts: `rich` on a capable desktop
 * (`supportsRichMotion()`) — the choreographed beats below; `fade`
 * everywhere else — a ≤150ms opacity fade, no 3D, no stagger; `none` under
 * reduced motion (and for a group that already played).
 *
 * Beats (`SettleKind`), rich mode:
 *   rise     — 16px rise + fade, 700ms ease-out; the default (greeting, copy
 *              lines, sections). 110ms per `order` step.
 *   stage    — a backdrop: fade + slow scale 1.06 → 1 over 900ms.
 *   case     — a hero cover case swings in from edge-on (rotateY -62°,
 *              x -56, y 18) on a soft spring with one small overshoot.
 *   railItem — one cover of a rail swings in (y 28, rotateY -35°), 800ms,
 *              90ms per `order` step; orders past `rail.cap` share its delay.
 *   row      — one row of a long list (a season's episodes): the rise beat
 *              on a tighter 45ms step, so 12 rows settle in ~1.2s; rows past
 *              `row.cap` render at rest.
 * Plus the fill beat (`<RevealFill>`): a progress bar's fill grows
 * scaleX 0 → 1 from the left over 900ms (CSS, `.settle-fill` in index.css).
 */
export type SettleMode = 'rich' | 'fade' | 'none';
export type SettleKind = 'rise' | 'stage' | 'case' | 'railItem' | 'row';

export const SETTLE = {
  /** One rise beat. */
  duration: 0.7,
  /** Delay per `order` step (the copy lines are 110ms apart). */
  stagger: 0.11,
  /** Rise distance, px. */
  rise: 16,
  /** The stage (backdrop) beat. */
  stageDuration: 0.9,
  stageScale: 1.06,
  /** The hero case's swing: from edge-on, its own perspective, hinged on the left edge. */
  caseSwing: { rotateY: -62, x: -56, y: 18, perspective: 1100, fadeDuration: 0.5 },
  /** Low stiffness = a slow, weighty swing; the damping leaves one small overshoot. */
  caseSpring: { type: 'spring', stiffness: 60, damping: 14, mass: 1 } as Transition,
  /** A rail's covers. */
  rail: { duration: 0.8, step: 0.09, cap: 5, rise: 28, rotateY: -35, perspective: 900 },
  /** A long list's rows: the rise beat, 45ms apart; past `cap` at rest. */
  row: { step: 0.045, cap: 12 },
  /** A progress bar's fill. */
  fillDuration: 0.9,
  /** The non-rich fade. */
  fadeDuration: 0.15,
  /** Orders past this render at rest. Not `railItem` (clamped) or `row` (`row.cap`). */
  maxOrder: 10,
} as const;

export function settleMode(): SettleMode {
  if (prefersReducedMotion()) return 'none';
  return supportsRichMotion() ? 'rich' : 'fade';
}

/** Delay (s) of step `order` of a beat, from its group's slot. `null` = render at rest. */
export function settleDelay(kind: SettleKind, order: number): number | null {
  if (kind === 'railItem') return Math.min(order, SETTLE.rail.cap) * SETTLE.rail.step;
  if (kind === 'row') return order > SETTLE.row.cap ? null : order * SETTLE.row.step;
  return order > SETTLE.maxOrder ? null : order * SETTLE.stagger;
}

/** Variants for one settling element. `delay` in seconds (rich mode only). */
export function settleVariants(
  mode: Exclude<SettleMode, 'none'>,
  delay: number,
  kind: SettleKind = 'rise',
): Variants {
  if (mode === 'fade') {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { duration: SETTLE.fadeDuration, ease: EASE_OUT } },
    };
  }
  switch (kind) {
    case 'stage':
      return {
        initial: { opacity: 0, scale: SETTLE.stageScale },
        animate: {
          opacity: 1,
          scale: 1,
          transition: {
            delay,
            duration: SETTLE.stageDuration,
            ease: EASE_OUT,
            opacity: { delay, duration: SETTLE.duration, ease: EASE_OUT },
          },
        },
      };
    case 'case': {
      const { rotateY, x, y, fadeDuration } = SETTLE.caseSwing;
      return {
        initial: { opacity: 0, rotateY, x, y },
        animate: {
          opacity: 1,
          rotateY: 0,
          x: 0,
          y: 0,
          transition: {
            ...SETTLE.caseSpring,
            delay,
            opacity: { delay, duration: fadeDuration, ease: EASE_OUT },
          },
        },
      };
    }
    case 'railItem':
      return {
        initial: { opacity: 0, y: SETTLE.rail.rise, rotateY: SETTLE.rail.rotateY },
        animate: {
          opacity: 1,
          y: 0,
          rotateY: 0,
          transition: { delay, duration: SETTLE.rail.duration, ease: EASE_OUT },
        },
      };
    default:
      return {
        initial: { opacity: 0, y: SETTLE.rise },
        animate: { opacity: 1, y: 0, transition: { delay, duration: SETTLE.duration, ease: EASE_OUT } },
      };
  }
}

/** The static style a beat needs on its element while it plays (rich mode only). */
export function settleStyle(kind: SettleKind): MotionStyle | undefined {
  switch (kind) {
    case 'stage':
      return { transformOrigin: '50% 100%' };
    case 'case':
      return { transformPerspective: SETTLE.caseSwing.perspective, transformOrigin: '0% 50%' };
    case 'railItem':
      return { transformPerspective: SETTLE.rail.perspective };
    default:
      return undefined;
  }
}
